import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const Reset = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error("Missing reset token");
    }
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Please log in.");
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid lg:grid-cols-2" data-testid="afri-reset-page">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1591561954557-26941169b49e?auto=format&fit=crop&q=80&w=1200"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#1A2024]/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <h2 className="font-display text-4xl leading-tight">
            Choose a new password and you&apos;re back in.
          </h2>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="font-display text-2xl mb-8 inline-block">
            AfriMarket
          </Link>
          <h1 className="font-display text-3xl mb-2">Set a new password</h1>
          <p className="text-muted-foreground text-sm mb-8">
            Make it at least 6 characters. Use something you&apos;ll remember.
          </p>

          {done ? (
            <div
              className="p-5 rounded-md border border-[#C05A3C]/30 bg-[#C05A3C]/5"
              data-testid="afri-reset-success"
            >
              <div className="font-semibold text-base mb-2">All set</div>
              <p className="text-sm text-muted-foreground">
                Redirecting you to login…
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="afri-reset-form">
              <div>
                <Label htmlFor="password" className="label-overline">New password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="afri-reset-password"
                  className="mt-2"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="label-overline">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="afri-reset-confirm"
                  className="mt-2"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !token}
                data-testid="afri-reset-submit"
                className="w-full h-12 bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Update password"
                )}
              </Button>
            </form>
          )}

          <p className="text-sm text-muted-foreground mt-6">
            Need a fresh link?{" "}
            <Link
              to="/forgot-password"
              className="text-[#C05A3C] font-medium hover:underline"
              data-testid="afri-reset-request-link"
            >
              Request a new one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Reset;
